<template>
	<div class="mobile-home-wrapper">
		<c-mobile-simple-scroll ref="scroll" direction="vertical">
			<!-- Banner 插槽，由父组件决定是否显示 -->
			<slot name="banner"></slot>

			<div v-if="unifiedItems.length" class="m-home-main">
				<div v-for="(item, index) in unifiedItems" :key="index" class="m-menu-item">
					<div class="m-menu-header">
						<span class="m-menu-title text-overflow">{{ item.label }}</span>
						<div class="m-menu-more" @click="item.onClick">
							<span class="m-menu-more-word">{{ $t('enterprise.realize-more') }}</span>
							<SvgIcons name="right-light" class="m-menu-more-icon" />
						</div>
					</div>

					<template v-if="item.module">
						<div class="m-module-item" :class="['module-id-' + item.module.moduleId]">
							<div
								class="m-module-wrap"
								:class="[
									useFreeStyle(item.module.moduleId) ? 'm-module-wrap--free' : 'm-module-wrap--normal'
								]"
							>
								<SmartDisplayC
									:key="getModuleKey(item.module)"
									:compId="getModuleId(item.module)"
									mode="mob-overview"
									v-bind="getModuleProps(item.module)"
								/>
							</div>
						</div>
					</template>
					<Empty v-else :text="$t('enterprise.no-module-data')" />
				</div>
			</div>
			<Empty v-else />

			<!-- Footer Slot -->
			<slot name="footer"></slot>
		</c-mobile-simple-scroll>
	</div>
</template>

<script setup>
import { computed, inject } from 'vue';
import { useCurrentContext } from './context';
import { useCommonData } from '@/hooks';
import { useStore } from '@/utils/vue-api';
import SmartDisplayC from '@rebuild/components/SmartDisplayC.vue';
import { useNavigation } from '@rebuild/hooks/useNavigation';
import { useModuleAttrs } from '@rebuild/hooks/useModuleAttrs';

const store = useStore();
const { getBackgroundImage, isFlowXTmpl } = useCommonData();

const isFlowXMobile = computed(() => store.getters.isMobileMode && isFlowXTmpl.value);

const templateMenus = inject('TEMPLATE_MENUS');
const getTemplateMenus = computed(() => templateMenus?.value || []);
const getFlowXHomeMenu = computed(() => {
	const homeMenu = getTemplateMenus.value.find(menu => menu.isHome);
	return homeMenu;
});

const context = useCurrentContext();
const formattedFlowMobMenus = context.formattedFlowMobMenus;

const unifiedItems = computed(() => {
	if (isFlowXMobile.value) {
		// 首页菜单 —— 组件平铺
		const homeMenu = getFlowXHomeMenu.value;
		return (homeMenu?.moduleVOS || []).map(module => ({
			label: module.name,
			onClick: () => handleToFlowXModule(module),
			module
		}));
	} else {
		// 菜单平铺
		return formattedFlowMobMenus(getTemplateMenus.value).map(menu => ({
			label: menu.name,
			onClick: () => handleMenuMore(menu),
			// 展示每个菜单下的第一个模块
			module: menu.moduleVOS.length ? menu.moduleVOS[0] : null
		}));
	}
});

const { viewModuleMoreInFlow, viewModuleMoreInFlowX } = useNavigation();
const handleToFlowXModule = module => {
	const menu = getFlowXHomeMenu.value;
	viewModuleMoreInFlowX(menu, module);
};
const handleMenuMore = menu => {
	if (!menu.id) return;
	viewModuleMoreInFlow(menu);
};

// 不需要应用圆角矩形外框的模块样式
const freeModules = ['analyst', 'news', 'report', 'performance_report', 'hk_report'];
const useFreeStyle = moduleId => {
	return freeModules.includes(moduleId);
};

const { getModuleId, getModuleProps, getModuleKey } = useModuleAttrs();

// 暴露核心数据和方法给父组件使用
defineExpose({
	getBackgroundImage
});
</script>

<style lang="less" scoped>
.mobile-home-wrapper {
	--side-padding: 14px;
	background: #f6f7f8;
	height: 100%;

	.m-home-banner {
		height: 120px;
		background-size: cover;
		background-position: center center;
		background-repeat: no-repeat;
	}
	.m-home-main {
		padding: 0 var(--side-padding) 16px var(--side-padding);
		overflow: hidden; // BFC
	}
	.m-menu-item {
		.m-menu-header {
			margin-top: 24px;
			margin-bottom: 12px;
			display: flex;
			justify-content: space-between;
			align-items: center;

			.m-menu-title {
				max-width: 229px;
				color: #1d2129;
				font-size: 18px;
				font-weight: 600;
				line-height: normal;
			}
			.m-menu-more {
				display: flex;
				align-items: center;

				.m-menu-more-word {
					color: #4e5969;
					text-align: center;
					font-size: 12px;
					font-weight: 400;
					line-height: 16px; /* 133.333% */
				}
				.m-menu-more-icon {
					margin-left: 2px;
					font-size: 16px;
					color: #4e5969 !important;
				}
			}
		}
	}
	.m-module-item {
		.m-module-wrap {
			&--free {
				margin: 0 calc(-1 * var(--side-padding));

				/deep/ .c-empty {
					background: #fff;
					border-radius: 12px;
				}
			}
			&--normal {
				padding: 16px;
				border-radius: 12px;
				background: #fff;
			}
		}
	}
}
</style>
